import { type Integrand, integrate } from "@/lib/tool-kit/calculus/integrate";

/**
 * Riemann sums and the Newton–Cotes rules built on the same equal-width partition
 * (Burden & Faires, Numerical Analysis, §4.3–4.4): with h = (b − a)/n and xᵢ = a + ih,
 *
 *     left       h·Σ f(xᵢ),            i = 0 … n−1
 *     right      h·Σ f(xᵢ),            i = 1 … n
 *     midpoint   h·Σ f(xᵢ₋½),          the midpoints
 *     trapezoid  (h/2)·[f(x₀) + 2Σ f(xᵢ) + f(xₙ)]
 *     Simpson    (h/3)·[f(x₀) + 4Σ f(x_odd) + 2Σ f(x_even) + f(xₙ)],  n even
 *
 * Every rule is the sum of weight × f(sample) times a common factor, which is how the
 * table is laid out. The exact value comes from adaptive quadrature, so the error shown
 * is against a reference accurate to far more digits than any of these sums.
 */

export type RiemannMethod =
  | "left"
  | "right"
  | "midpoint"
  | "trapezoid"
  | "simpson";

export const MAX_SUBINTERVALS = 1000;

export interface RiemannRow {
  /** Index of the sample point (midpoints are numbered 1…n). */
  index: number;
  x: number;
  fx: number;
  /** Multiplier of f(x) inside the bracket. */
  weight: number;
}

export type RiemannResult =
  | {
      kind: "ok";
      method: RiemannMethod;
      /** Subintervals actually used (Simpson needs an even number). */
      n: number;
      /** The n that was asked for, when it had to change. */
      requested: number;
      h: number;
      /** The common factor in front of the weighted sum: h, h/2 or h/3. */
      factor: number;
      rows: RiemannRow[];
      sum: number;
      /** Reference value from adaptive quadrature; NaN if that did not converge. */
      exact: number;
      /** sum − exact. */
      error: number;
    }
  | { kind: "undefined"; at: number };

function weightsFor(method: RiemannMethod, n: number): number[] {
  switch (method) {
    case "left":
      return Array.from({ length: n + 1 }, (_, i) => (i < n ? 1 : 0));
    case "right":
      return Array.from({ length: n + 1 }, (_, i) => (i > 0 ? 1 : 0));
    case "trapezoid":
      return Array.from({ length: n + 1 }, (_, i) =>
        i === 0 || i === n ? 1 : 2
      );
    case "simpson":
      return Array.from({ length: n + 1 }, (_, i) => {
        if (i === 0 || i === n) {
          return 1;
        }
        return i % 2 === 1 ? 4 : 2;
      });
    default:
      return [];
  }
}

function factorFor(method: RiemannMethod, h: number): number {
  if (method === "trapezoid") {
    return h / 2;
  }
  return method === "simpson" ? h / 3 : h;
}

/** The sample points and weights of a rule, without evaluating anything. */
export function riemannPoints(
  method: RiemannMethod,
  a: number,
  b: number,
  n: number
): { x: number; weight: number; index: number }[] {
  const h = (b - a) / n;
  if (method === "midpoint") {
    return Array.from({ length: n }, (_, i) => ({
      index: i + 1,
      x: a + (i + 0.5) * h,
      weight: 1,
    }));
  }
  const weights = weightsFor(method, n);
  return weights
    .map((weight, i) => ({ index: i, x: a + i * h, weight }))
    .filter((point) => point.weight !== 0);
}

/** The rule's value for `n` subintervals (no table, no reference). */
export function riemannSum(
  f: Integrand,
  method: RiemannMethod,
  a: number,
  b: number,
  n: number
): number {
  const h = (b - a) / n;
  let total = 0;
  for (const point of riemannPoints(method, a, b, n)) {
    total += point.weight * f(point.x);
  }
  return factorFor(method, h) * total;
}

/** n for the rule: Simpson's rule needs an even count, so an odd request goes up by one. */
export function effectiveN(method: RiemannMethod, requested: number): number {
  const n = Math.max(1, Math.min(MAX_SUBINTERVALS, Math.floor(requested)));
  return method === "simpson" && n % 2 === 1
    ? Math.min(n + 1, MAX_SUBINTERVALS)
    : n;
}

export function riemann(
  f: Integrand,
  method: RiemannMethod,
  a: number,
  b: number,
  requested: number
): RiemannResult {
  const n = effectiveN(method, requested);
  const h = (b - a) / n;
  const rows: RiemannRow[] = [];
  let weighted = 0;
  for (const point of riemannPoints(method, a, b, n)) {
    const fx = f(point.x);
    if (!Number.isFinite(fx)) {
      return { kind: "undefined", at: point.x };
    }
    rows.push({ index: point.index, x: point.x, fx, weight: point.weight });
    weighted += point.weight * fx;
  }
  const factor = factorFor(method, h);
  const sum = factor * weighted;
  const reference = integrate(f, a, b);
  const exact = reference.converged ? reference.value : Number.NaN;
  return {
    kind: "ok",
    method,
    n,
    requested: Math.floor(requested),
    h,
    factor,
    rows,
    sum,
    exact,
    error: sum - exact,
  };
}

/** The rule's value and error at n = 4, 8, 16, … so the order of convergence is visible. */
export function convergenceTable(
  f: Integrand,
  method: RiemannMethod,
  a: number,
  b: number,
  exact: number,
  counts: number[] = [4, 8, 16, 32, 64, 128]
): { n: number; sum: number; error: number }[] {
  return counts.map((n) => {
    const sum = riemannSum(f, method, a, b, n);
    return { n, sum, error: sum - exact };
  });
}
